/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";

/**
 * Ping/keepalive manager for the WebSocket connection.
 *
 * **REQ-TRANS-06:** Periodic keepalive pings with configurable interval.
 * - Default interval: 30 seconds.
 * - Server can override via `x-tivo-ping-interval` header.
 * - Hangup timeout: ping_interval + 10 seconds.
 * - No jitter applied to ping interval.
 * - Interval capped to max unsigned 16-bit integer (65535).
 */
export class WebSocketPingManager {
  private static readonly TAG: string = "WebSocketPingManager";

  private static readonly DEFAULT_PING_INTERVAL_SECONDS: number = 600;
  private static readonly HANGUP_BUFFER_SECONDS: number = 10;
  private static readonly MAX_INTERVAL_SECONDS: number = 65535;
  private static readonly PING_INTERVAL_HEADER_MAX_LENGTH: number = 10;

  private mPingIntervalSeconds: number;
  private mPingTimer: ReturnType<typeof setInterval> | null = null;
  private mHangupTimer: ReturnType<typeof setTimeout> | null = null;
  private mLastTrafficTimestamp: number = 0;
  private mOnPing: (() => void) | null = null;
  private mOnHangup: (() => void) | null = null;

  constructor() {
    this.mPingIntervalSeconds =
      WebSocketPingManager.DEFAULT_PING_INTERVAL_SECONDS;
  }

  /**
   * Schedule a one-shot hangup check. Fires once; if hangup detected,
   * invokes onHangup and does NOT reschedule (avoids repeated close calls).
   * If traffic was recorded, reschedules.
   */
  private scheduleHangupCheck(hangupTimeoutMs: number): void {
    if (this.mHangupTimer !== null) {
      clearTimeout(this.mHangupTimer);
      this.mHangupTimer = null;
    }
    this.mHangupTimer = setTimeout((): void => {
      this.mHangupTimer = null;
      const elapsed: number = Date.now() - this.mLastTrafficTimestamp;
      if (elapsed >= hangupTimeoutMs) {
        Log.w(
          WebSocketPingManager.TAG,
          `Hangup timeout: no traffic for ${Math.round(elapsed / 1000)}s (limit: ${Math.round(hangupTimeoutMs / 1000)}s)`
        );
        if (this.mOnHangup !== null) {
          this.mOnHangup();
        }
        // Do NOT reschedule — hangup already fired
      } else {
        // Traffic was received since last check — reschedule
        this.scheduleHangupCheck(hangupTimeoutMs);
      }
    }, hangupTimeoutMs);
  }

  /**
   * Parse and set the ping interval from the server's response header value.
   *
   * The `x-tivo-ping-interval` header value is capped at 10 characters.
   * If absent, zero, negative, or unparseable, falls back to the default (600s).
   * If the value exceeds 65535, it is clamped to the default.
   *
   * @param headerValue - The raw header value string, or undefined if absent
   */
  public setPingIntervalFromHeader(headerValue: string | undefined): void {
    if (headerValue === undefined || headerValue === null) {
      this.mPingIntervalSeconds =
        WebSocketPingManager.DEFAULT_PING_INTERVAL_SECONDS;
      Log.d(
        WebSocketPingManager.TAG,
        `setPingIntervalFromHeader(): No header, using default ${this.mPingIntervalSeconds}s`
      );
      return;
    }

    const trimmed: string = headerValue
      .substring(0, WebSocketPingManager.PING_INTERVAL_HEADER_MAX_LENGTH)
      .trim();
    const parsed: number = parseInt(trimmed, 10);

    if (!Number.isFinite(parsed) || parsed <= 0) {
      this.mPingIntervalSeconds =
        WebSocketPingManager.DEFAULT_PING_INTERVAL_SECONDS;
      Log.d(
        WebSocketPingManager.TAG,
        `setPingIntervalFromHeader(): Invalid value "${trimmed}", using default ${this.mPingIntervalSeconds}s`
      );
      return;
    }

    if (parsed > WebSocketPingManager.MAX_INTERVAL_SECONDS) {
      this.mPingIntervalSeconds =
        WebSocketPingManager.DEFAULT_PING_INTERVAL_SECONDS;
      Log.d(
        WebSocketPingManager.TAG,
        `setPingIntervalFromHeader(): Value ${parsed} exceeds max, using default ${this.mPingIntervalSeconds}s`
      );
      return;
    }

    this.mPingIntervalSeconds = parsed;
    Log.d(
      WebSocketPingManager.TAG,
      `setPingIntervalFromHeader(): Using server interval ${this.mPingIntervalSeconds}s`
    );
  }

  /**
   * Start the ping/keepalive cycle.
   *
   * @param onPing - Callback invoked when a ping frame should be sent
   * @param onHangup - Callback invoked when the hangup timeout expires (no traffic received)
   */
  public start(onPing: () => void, onHangup: () => void): void {
    this.stop();

    this.mOnPing = onPing;
    this.mOnHangup = onHangup;
    this.mLastTrafficTimestamp = Date.now();

    const pingIntervalMs: number = this.mPingIntervalSeconds * 1000;
    const hangupTimeoutMs: number =
      (this.mPingIntervalSeconds + WebSocketPingManager.HANGUP_BUFFER_SECONDS) *
      1000;

    Log.d(
      WebSocketPingManager.TAG,
      `start(): pingInterval=${this.mPingIntervalSeconds}s, hangupTimeout=${this.mPingIntervalSeconds + WebSocketPingManager.HANGUP_BUFFER_SECONDS}s`
    );

    // Periodic ping timer
    this.mPingTimer = setInterval((): void => {
      if (this.mOnPing !== null) {
        this.mOnPing();
      }
    }, pingIntervalMs);

    // Hangup detection — one-shot timer, rescheduled on recordTraffic()
    this.scheduleHangupCheck(hangupTimeoutMs);
  }

  /**
   * Stop all ping and hangup timers.
   */
  public stop(): void {
    if (this.mPingTimer !== null) {
      clearInterval(this.mPingTimer);
      this.mPingTimer = null;
    }
    if (this.mHangupTimer !== null) {
      clearTimeout(this.mHangupTimer);
      this.mHangupTimer = null;
    }
    this.mOnPing = null;
    this.mOnHangup = null;
    Log.d(WebSocketPingManager.TAG, "stop(): Timers cleared");
  }

  /**
   * Record that valid traffic has been received.
   * Resets the hangup deadline.
   */
  public recordTraffic(): void {
    this.mLastTrafficTimestamp = Date.now();
  }

  /**
   * Get the current active ping interval in seconds.
   */
  public getPingIntervalSeconds(): number {
    return this.mPingIntervalSeconds;
  }
}
